/**
 * Team management API client.
 */

import { supabase } from './supabase';

const API_URL =
  import.meta.env.VITE_API_URL ??
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8787'
    : 'https://api.waypoint-intel.com');

async function authHeader(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${session.access_token}` };
}

export type Role = 'owner' | 'admin' | 'member';

export interface TeamMember {
  user_id: string;
  email: string | null;
  role: Role;
  joined_at: string;
  is_self: boolean;
  granted_scopes: string[];
  /** workload_balance: chance they miss a due date on their tasks this week. Null unless a trained model serves it. */
  missWeekRisk?: number | null;
}

export interface TeamInvite {
  id: string;
  email: string;
  role: Role;
  invited_at: string;
  expires_at: string;
  invited_by: string | null;
  accepted_at: string | null;
}

export interface TeamSummary {
  my_role: Role | null;
  members: TeamMember[];
  invites: TeamInvite[];
}

export async function getTeam(): Promise<TeamSummary> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/team`, { headers });
  if (!res.ok) throw new Error(`API ${res.status}: ${(await res.text()).slice(0, 200)}`);
  return (await res.json()) as TeamSummary;
}

export async function inviteMember(email: string, role: 'admin' | 'member' = 'member'): Promise<{ ok: true }> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/team/invite`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error || `API ${res.status}`);
  }
  return (await res.json()) as { ok: true };
}

/** Unauthenticated: trade the invite token for a fresh one-time sign-in token. */
export async function redeemInvite(token: string): Promise<{ token_hash: string; type: 'invite' | 'magiclink' }> {
  const res = await fetch(`${API_URL}/v1/team/invites/redeem`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throw new Error(await apiError(res));
  return res.json();
}

export async function acceptInvite(token: string): Promise<{ account_id: string }> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/team/accept`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error || `API ${res.status}`);
  }
  return (await res.json()) as { account_id: string };
}

async function apiError(res: Response): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error || `API ${res.status}`;
}

export interface Workspace { account_id: string; name: string; role: Role | 'viewer' }

export async function listWorkspaces(): Promise<{ active_account_id: string; workspaces: Workspace[] }> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/team/workspaces`, { headers });
  if (!res.ok) throw new Error(await apiError(res));
  return res.json();
}

/** Server verifies membership, then stores it as the user's primary workspace. */
export async function switchWorkspace(accountId: string): Promise<void> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/team/workspaces/switch`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ account_id: accountId }),
  });
  if (!res.ok) throw new Error(await apiError(res));
}

export async function leaveWorkspace(): Promise<void> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/team/leave`, { method: 'POST', headers });
  if (!res.ok) throw new Error(await apiError(res));
}

export async function removeMember(userId: string): Promise<void> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/team/${userId}`, { method: 'DELETE', headers });
  if (!res.ok) throw new Error(await apiError(res));
}

export async function cancelInvite(inviteId: string): Promise<void> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/team/invite/${inviteId}`, { method: 'DELETE', headers });
  if (!res.ok) throw new Error(`API ${res.status}`);
}

/** Set additional view-scope grants on a member (e.g. ['team'] or ['team','org']). */
export async function setMemberGrants(userId: string, grantedScopes: ('team'|'org')[]): Promise<{ member: { user_id: string; granted_scopes: string[] } }> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/team/${userId}/grants`, {
    method: 'PATCH',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ granted_scopes: grantedScopes }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error || `API ${res.status}`);
  }
  return (await res.json()) as { member: { user_id: string; granted_scopes: string[] } };
}
